import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  /**
   * El nombre de la pantalla. Acepta un nodo porque hay encabezados donde el
   * título ES un control —Leads abre con el embudo que se está mirando, y se
   * cambia desde ahí—; sigue viviendo dentro del mismo `h1`, así que para quien
   * navega con lector de pantalla no cambia nada.
   */
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: readonly Crumb[];
  className?: string;
}

/** Top band of a panel: where you are, what this is, and what you can do here. */
export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'flex shrink-0 flex-wrap items-end justify-between gap-x-4 gap-y-3 border-b border-border px-6 py-4',
        className,
      )}
    >
      {/* `flex-1`: el bloque del título toma el espacio libre en vez de medir
          su propio contenido. Sin eso, un título que ES un control —Leads abre
          con el embudo— se recortaba a mitad de palabra con media cabecera
          vacía al lado, porque el ancho salía del contenido y el contenido se
          recortaba a sí mismo. Las acciones no se mueven: ya eran `shrink-0`. */}
      <div className="min-w-0 flex-1">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Ruta de navegación" className="mb-1.5">
            <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
              {breadcrumbs.map((crumb, index) => (
                <Fragment key={`${crumb.label}-${index}`}>
                  {index > 0 && (
                    <li aria-hidden className="flex items-center">
                      <ChevronRight className="size-3" />
                    </li>
                  )}
                  <li>
                    {crumb.to ? (
                      <Link
                        to={crumb.to}
                        className="rounded-sm transition-opacity duration-150 ease-out hover:text-foreground"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span aria-current="page" className="text-foreground">
                        {crumb.label}
                      </span>
                    )}
                  </li>
                </Fragment>
              ))}
            </ol>
          </nav>
        )}

        <h1 className="truncate text-2xl font-medium tracking-[-0.01em]">{title}</h1>

        {description && (
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
        )}
      </div>

      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </header>
  );
}
